import { Avatar, Box, Grid, IconButton, Typography, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import toast from 'react-hot-toast';
import { passwordRequirements } from '../../../utils/userInputUtils';
import LabeledInput from '../../Common/LabeledInput';
import StyledBackButton from '../../Common/StyledBackButton';
import StyledButton from '../../Common/StyledButton';
import { useAppSelector } from '../../../redux/hooks';
import uiConfig from '../../../config/uiConfig';
import { UserStatusType } from '../../../types/coach';
import AddNewCoachModal from '../../AddNewCoach/AddNewCoachModal';
import { ARCHIVE_COACH_MUTATION } from '../../../service/graphql/coachQuery';
import { IArchiveCoachPayload } from '../../../redux/coach/coachSlice';
import { toastStyles } from '../../../utils/utils';

interface ICoachArchiveModalProps {
  coachId: string;
  handleSuccess: () => void;
  handleDismiss: () => void;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture: string;
  title: string;
  subTitle: string;
  btnText: string;
  icon: string;
  bgcolor: string;
  isOpen: boolean;
}

const StyledGrid = styled(Grid)<{ bgcolor: string }>(({ bgcolor }) => ({
  background: bgcolor,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '11px',
}));

function CoachArchiveNewModal({
  coachId,
  handleSuccess,
  handleDismiss,
  firstName,
  lastName,
  email,
  profilePicture,
  title,
  subTitle,
  btnText,
  icon,
  bgcolor,
  isOpen,
}: ICoachArchiveModalProps) {
  const [archiveCoach, { data, error, loading, called }] = useMutation<
    unknown,
    { archiveCoachInput: IArchiveCoachPayload }
  >(ARCHIVE_COACH_MUTATION);
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [allValid, setAllValid] = useState(false);
  const userStatusType = useAppSelector((state) => state.coach.coach?.userStatusType);

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };
  const handleDeleteClick = () => {
    archiveCoach({ variables: { archiveCoachInput: { coachId, password } } });
  };

  useEffect(() => {
    const isAllValid = passwordRequirements(password, password).every((requirement) =>
      requirement.isValid()
    );
    setAllValid(isAllValid);
  }, [password]);

  useEffect(() => {
    if (!called) return;
    if (loading) return;
    if (error) toast.success(error.message, toastStyles.error);
    if (data) {
      toast.success(t('coachesTable.archiveCoach.toastMessage'), toastStyles.success);
      handleSuccess();
    }
  }, [data, error, called, loading]);

  const randomIndex = Math.floor(Math.random() * uiConfig.avatarBackgroundColors.length);
  const randomBackgroundColor = uiConfig.avatarBackgroundColors[randomIndex];

  return (
    <AddNewCoachModal isOpen={isOpen} onClose={handleDismiss} xs={11} md={3.8}>
      <Grid container maxHeight={709} flexDirection="column" bgcolor="background.paper">
        <StyledGrid item height={167} bgcolor={bgcolor}>
          <img src={icon} alt="bin" role="presentation" height={56} />
          <Typography variant="title2Bold" color="primary" textAlign="center">
            {title}
          </Typography>
        </StyledGrid>
        <Grid item maxHeight={542} px={4} pt={6.5} pb={4.5}>
          <Grid container spacing={5} flexDirection="column">
            <Grid item textAlign="center">
              <Typography variant="bodyRegular" color="primary" sx={{ letterSpacing: '-0.41px' }}>
                {subTitle}
              </Typography>
            </Grid>
            <Grid item display="flex" flexDirection="column" gap="16px">
              <Box display="flex" alignItems="center" justifyContent="center">
                {profilePicture ? (
                  <img
                    src={
                      profilePicture.startsWith('https')
                        ? profilePicture
                        : `${process.env.REACT_APP_MEDIA_FILES_BASE_URL}${
                            profilePicture.startsWith('/') ? '' : '/'
                          }${profilePicture}`
                    }
                    alt="user-avatar"
                    loading="lazy"
                    style={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  // eslint-disable-next-line react/jsx-no-undef
                  <Avatar
                    sx={{ width: '72px', height: '72px', bgcolor: randomBackgroundColor }}
                  >{`${firstName[0]}${lastName[0]}`}</Avatar>
                )}
              </Box>
              <Box display="flex" alignItems="center" flexDirection="column" gap="4px">
                <Typography variant="title3Regular" color="primary" textAlign="center">
                  {`${firstName} ${lastName}`}
                </Typography>
                <Typography variant="bodyRegular" color="#001E1D" textAlign="right">
                  {email}
                </Typography>
              </Box>
            </Grid>
            {userStatusType !== UserStatusType.EXTERNAL_PROVIDER && (
              <Grid item>
                <LabeledInput
                  label={t('auth.password')}
                  required
                  name="newPassword"
                  placeholder={t('auth.password')}
                  type={showPassword ? 'text' : 'password'}
                  id="new-password"
                  value={password}
                  handleChange={handleChange}
                  startIcon={<img src="/assets/icons/security.svg" alt="email" />}
                  endIcon={
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handlePasswordVisibility}
                    >
                      {showPassword ? (
                        <img src="/assets/icons/visibility.svg" alt="visibility" />
                      ) : (
                        <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
                      )}
                    </IconButton>
                  }
                />
              </Grid>
            )}
            <Grid item>
              <Grid container spacing={2}>
                <Grid item sm={6} width="100%">
                  <StyledBackButton
                    variant="outlined"
                    onClick={handleDismiss}
                    disabled={loading}
                    fullWidth
                  >
                    {t('common.dismiss')}
                  </StyledBackButton>
                </Grid>
                <Grid item sm={6} width="100%">
                  <StyledButton
                    onClick={handleDeleteClick}
                    disabled={userStatusType !== UserStatusType.EXTERNAL_PROVIDER && !allValid}
                    loading={loading}
                    paddingY={14}
                    fullWidth
                  >
                    {btnText}
                  </StyledButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AddNewCoachModal>
  );
}

export default CoachArchiveNewModal;
